import { useMemo } from 'react';
import { Button, Flex, Select, Stack, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	BulkAssignCounterpartiesPreviewRoleRes,
	ProgrammeUserWithPermissionRolesRes,
} from '@dealstack/shared/lib/generated';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

export const BulkAssignRoleSection = ({
	role,
	totalDocuments,
	programmeUsers,
	changes,
	onAssignChange,
	onToggleUnassign,
}: {
	role: BulkAssignCounterpartiesPreviewRoleRes;
	totalDocuments: number;
	programmeUsers: ProgrammeUserWithPermissionRolesRes[];
	changes?: { assignUserId: string | null; unassignUserIds: string[] };
	onAssignChange: (userId: string | null) => void;
	onToggleUnassign: (userId: string) => void;
}) => {
	const tMipManage = useTranslationMipManage();

	const userOptions = useMemo(() => {
		const fullyAssignedUserIds = new Set(
			role.currentAssignees
				.filter((a) => a.documentCount >= role.documentCount)
				.map((a) => a.userId)
		);
		return programmeUsers
			.filter((user) => !fullyAssignedUserIds.has(user.id))
			.map((user) => ({
				value: user.id,
				label: `${user.firstName} ${user.lastName}`,
			}));
	}, [programmeUsers, role.currentAssignees, role.documentCount]);

	const hasConflict = role.currentAssignees.length > 1;

	return (
		<Stack
			gap="xs"
			p="sm"
			sx={(theme) => ({
				border: `1px solid ${theme.colors.gray[2]}`,
				borderRadius: theme.radius.sm,
			})}
		>
			<Flex justify="space-between" align="center">
				<Text fw={600} size="sm">
					{role.roleName}
				</Text>
				<Text size="sm">
					{tMipManage('documents_page.bulk_assign_counterparty_modal.applies_to', {
						count: role.documentCount,
						total: totalDocuments,
					})}
				</Text>
			</Flex>
			{hasConflict && (
				<Text size="xs" c="orange">
					{tMipManage('documents_page.bulk_assign_counterparty_modal.conflict_warning')}
				</Text>
			)}
			{role.invitedCount > 0 && (
				<Text size="xs" c="dimmed">
					{tMipManage(
						'documents_page.bulk_assign_counterparty_modal.invited_slots_hint',
						{ count: role.invitedCount }
					)}
				</Text>
			)}
			{role.currentAssignees.map((assignee) => {
				const canUnassign = assignee.documentCount > assignee.signedDocumentCount;
				const isMarkedForUnassign =
					changes?.unassignUserIds.includes(assignee.userId) ?? false;

				return (
					<Flex
						key={assignee.userId}
						justify="space-between"
						align="center"
						p="xs"
						sx={(theme) => ({
							backgroundColor: isMarkedForUnassign
								? theme.colors.red[0]
								: theme.colors.gray[0],
							borderRadius: theme.radius.sm,
						})}
					>
						<Flex direction="column">
							<Text size="sm">
								{assignee.firstName} {assignee.lastName}
							</Text>
							<Text size="xs">
								{tMipManage(
									'documents_page.bulk_assign_counterparty_modal.assigned_on_docs',
									{ count: assignee.documentCount }
								)}
								{assignee.signedDocumentCount > 0 &&
									` (${tMipManage(
										'documents_page.bulk_assign_counterparty_modal.already_signed',
										{ count: assignee.signedDocumentCount }
									)})`}
							</Text>
						</Flex>
						{canUnassign && (
							<Button
								variant={isMarkedForUnassign ? 'filled' : 'subtle'}
								color={isMarkedForUnassign ? 'red' : undefined}
								size="xs"
								onClick={() => onToggleUnassign(assignee.userId)}
							>
								{isMarkedForUnassign
									? tMipManage(
											'documents_page.bulk_assign_counterparty_modal.undo_unassign'
										)
									: tMipManage(
											'documents_page.bulk_assign_counterparty_modal.unassign'
										)}
							</Button>
						)}
					</Flex>
				);
			})}
			{role.unassignedCount > 0 && (
				<Select
					data-testid={TEST_ELEMENT_IDS.BULK_ASSIGN_COUNTERPARTY_ROLE_SELECT}
					placeholder={tMipManage(
						'documents_page.bulk_assign_counterparty_modal.user_placeholder'
					)}
					data={userOptions}
					value={changes?.assignUserId ?? null}
					onChange={onAssignChange}
					searchable
					clearable
					nothingFoundMessage={tMipManage(
						'documents_page.bulk_assign_counterparty_modal.no_users_found'
					)}
					comboboxProps={{ withinPortal: true, zIndex: Z_INDEX.MODAL + 1 }}
				/>
			)}
		</Stack>
	);
};
